import React from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { TariffRateRow } from '../fixtures';

interface Props {
  open: boolean;
  /** The charges added this session (draft rows) that are about to be saved. */
  charges?: TariffRateRow[];
  /** A still-missing customer FSC tariff being assigned this session. */
  tariff?: { tariffNumber: number; rate?: string | null } | null;
  /** Disables both buttons while the save is in flight. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation shown before saving a change that becomes PERMANENT on a SECURED
 * quote. A secured quote is otherwise locked, but two deviations are allowed:
 * ADDING additional charges, and ASSIGNING a still-missing customer FSC tariff.
 * Neither can be changed once saved (create a new revision instead), so the user
 * confirms first. Lists whichever change(s) are staged. Portals to <body>
 * (z-[120]) so it escapes the editor drawer's transformed/scrolled panel.
 */
const ConfirmSecuredChangesModal: React.FC<Props> = ({
  open, charges = [], tariff = null, busy = false, onConfirm, onCancel,
}) => {
  // Alt+S confirms, Alt+C cancels. Confirm DOES get a chord here — unlike the
  // generic warn-and-continue modal — because this gate only ever means "yes,
  // save the secured-quote changes I already asked to save", not a discard.
  useAltHotkeys(
    { S: busy ? null : onConfirm, C: busy ? null : onCancel },
    undefined,
    { layer: open },
  );

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;
  const hasCharges = charges.length > 0;
  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/40 p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Confirm changes to secured quote"
      onClick={busy ? undefined : onCancel}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="w-full max-w-[460px] rounded-lg border border-slate-200 bg-panel shadow-xl dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={
            <span className="flex items-center gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 text-amber-400" />
              Confirm changes to secured quote
            </span>
          }
          onClose={busy ? undefined : onCancel}
          dragHandleProps={dragHandleProps}
        />
        <div className="px-4 py-4 text-[0.78125rem] text-slate-700 dark:text-slate-200">
          {tariff && (
            <div className="mb-3">
              <p className="mb-2">You&rsquo;re assigning a fuel-surcharge tariff to this secured quote:</p>
              <div className="rounded border border-slate-200 px-2.5 py-1.5 dark:border-slate-700">
                <span className="font-semibold text-slate-900 dark:text-slate-100">Cust Tariff #{tariff.tariffNumber}</span>
                {tariff.rate ? (
                  <span className="ml-2 text-[0.71875rem] text-slate-500 dark:text-slate-400">rate {tariff.rate}</span>
                ) : null}
              </div>
            </div>
          )}
          {hasCharges && (
            <>
              <p className="mb-2">
                You&rsquo;re adding the following charge{charges.length === 1 ? '' : 's'} to this secured quote:
              </p>
              <ul className="mb-3 divide-y divide-slate-100 rounded border border-slate-200 dark:divide-slate-700 dark:border-slate-700">
                {charges.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3 px-2.5 py-1.5">
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{c.description || '—'}</span>
                    <span className="flex-shrink-0 text-[0.71875rem] text-slate-500 dark:text-slate-400">
                      {c.rateType || '—'} &middot; ${c.rate || '0'}
                      {c.freeTime && Number(c.freeTime) > 0 ? ` · ${c.freeTime} ${c.freeTimeUnit || ''}`.trimEnd() : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="rounded bg-amber-50 px-2.5 py-2 text-[0.75rem] text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            Once you save, this becomes permanent &mdash; you won&rsquo;t be able to change it on this secured
            quote. Create a new revision to make further changes.
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded border border-slate-300 bg-panel px-3 py-1.5 text-[0.75rem] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:text-slate-200 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded border border-blue-700 bg-blue-600 px-3 py-1.5 text-[0.75rem] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Confirm & Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ConfirmSecuredChangesModal;
